import type { CashDenominationRowDto } from './cash-denominations.dto.js';

export const cashTreasuryCountModeValues = ['normal', 'starting'] as const;
export type CashTreasuryCountMode = (typeof cashTreasuryCountModeValues)[number];

export const cashTreasuryCountDraftStatusValues = [
  'active',
  'completed',
  'cancelled',
  'expired',
] as const;
export type CashTreasuryCountDraftStatus = (typeof cashTreasuryCountDraftStatusValues)[number];

export const cashTreasuryCountStatusValues = ['recorded'] as const;
export type CashTreasuryCountStatus = (typeof cashTreasuryCountStatusValues)[number];

export const cashTreasuryCountDiscrepancyStatusValues = ['open', 'resolved'] as const;
export type CashTreasuryCountDiscrepancyStatus =
  (typeof cashTreasuryCountDiscrepancyStatusValues)[number];

export interface CashTreasuryCountDraftDto {
  id: string;
  companyId: string;
  treasuryCashLocationId: string;
  mode: CashTreasuryCountMode;
  status: CashTreasuryCountDraftStatus;
  startedByUserId: string;
  startedAt: string;
  expiresAt: string;
  expectedBalanceMinor: number;
  expectedBalanceAsOf: string;
  ledgerMovementCount: number;
  ledgerLastMovementAt: string | null;
  ledgerLastMovementUpdatedAt: string | null;
  version: number;
  completedCountId: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledByUserId: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CashTreasuryCountDiscrepancyDto {
  id: string;
  companyId: string;
  countId: string;
  treasuryCashLocationId: string;
  expectedAmountMinor: number;
  countedAmountMinor: number;
  differenceMinor: number;
  status: CashTreasuryCountDiscrepancyStatus;
  createdByUserId: string;
  createdAt: string;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  resolutionNote: string | null;
  resolutionMovementId: string | null;
}

export interface CashTreasuryCountDto {
  id: string;
  companyId: string;
  treasuryCashLocationId: string;
  draftId: string;
  mode: CashTreasuryCountMode;
  status: CashTreasuryCountStatus;
  expectedBalanceMinor: number;
  expectedBalanceAsOf: string;
  countedAmountMinor: number;
  differenceMinor: number;
  denominations: CashDenominationRowDto[] | null;
  sourceSnapshotId: string | null;
  sourceBaselineId: string | null;
  countedByUserId: string;
  countedAt: string;
  note: string | null;
  idempotencyKey: string;
  createdAt: string;
  updatedAt: string;
  discrepancy: CashTreasuryCountDiscrepancyDto | null;
}

export interface StartCashTreasuryCountRequest {
  companyId: string;
  operationId: string;
  treasuryCashLocationId: string;
  mode: CashTreasuryCountMode;
  occurredAt?: string;
}

export interface StartCashTreasuryCountResponse {
  draft: CashTreasuryCountDraftDto;
  expectedBalanceMinor: number;
  lockToken: string;
  denominationRowsRequired: boolean;
  denominationInventoryActive: boolean;
}

export interface CompleteCashTreasuryCountRequest {
  companyId: string;
  operationId: string;
  draftId: string;
  countedAmountMinor: number;
  denominations?: CashDenominationRowDto[] | null;
  lockToken: string;
  version: number;
  idempotencyKey: string;
  note?: string | null;
  occurredAt?: string;
}

export interface CompleteCashTreasuryCountResponse {
  count: CashTreasuryCountDto;
}

export interface CancelCashTreasuryCountRequest {
  companyId: string;
  operationId: string;
  draftId: string;
  lockToken?: string;
  override?: boolean;
  reason?: string | null;
  occurredAt?: string;
}

export interface CancelCashTreasuryCountResponse {
  draft: CashTreasuryCountDraftDto;
}

export interface ListCashTreasuryCountsRequest {
  companyId: string;
  treasuryCashLocationId?: string;
  mode?: CashTreasuryCountMode;
  from?: string;
  to?: string;
}

export interface ListCashTreasuryCountsResponse {
  items: CashTreasuryCountDto[];
}

export interface GetCashTreasuryCountRequest {
  companyId: string;
  countId: string;
}

export interface GetCashTreasuryCountResponse {
  count: CashTreasuryCountDto;
}
